/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2018, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../lib/@waca/core-client/js/core-client/ui/core/Class',
	'../../../app/nls/StringResources',
	'./ThumbnailStore',
	'../../../lib/@waca/core-client/js/core-client/utils/UniqueId',
	'../../../lib/@waca/image-capture/dist/js/bundles/image-capture.min',
	'../../../lib/@waca/echo-cache/dist/js/bundles/echo-cache.min',
	'../../../lib/@waca/core-client/js/core-client/utils/ClassFactory',
	'underscore',
	'jquery'
], function(Class, StringResources, ThumbnailStore, UniqueId, ImageCaptureLib, EchoCacheLib, ClassFactory, _, $) {

	/**
	 * Unique DOM attributes to strip.
	 * Every time a new thumbnail is generated, it contains unique bits of
	 * information. For performance reasons, we need to compare the existing
	 * thumbnail data with the one about to be stored. The unique attributes
	 * need to be stripped for the compared data, or the comparison will always
	 * yield false positives.
	 * @type {Array}
	 */
	const UNIQUE_DOM_ATTRIBUTES = [
		'id',
		'clip-path'
	];

	const ImageCapture = ImageCaptureLib.default;
	const EchoCacheService = EchoCacheLib.EchoCacheService;

	const ThumbnailService = Class.extend({

		/**
		 * local store
		 * serves as a cache to store all thumbnails
		 * @type {ThumbnailStore}
		 */
		_store: null,

		/**
		 * Echo cache service
		 *
		 * @type {EchoCacheService}
		 */
		echoCacheService: null,

		/**
		 * Thumbnail service class that has all the relevant logic regarding
		 * the persistence of thumbnails
		 *
		 * @param {Object} [options] - options
		 * @param {Object} [options.store] - store to initialize the instance with
		 */
		init: function(options = {}) {
			ThumbnailService.inherited('init', this, arguments);
			this._pendingRequests = {
				fetch: {}
			};
			this.logger = options.dashboardApi.getFeature('Logger');
			this.ajax = options.dashboardApi.getFeature('Ajax');
			this.readyPromise = this.initialize(options);
		},

		/**
		 * Check whether the pending request type is valid or not
		 *
		 * @param {string} type - pending request type to validate
		 *
		 * @return {boolean} TRUE if pending request type is valid and FALSE if not
		 */
		_isValidPendingRequestType: function(type) {
			return !!this._pendingRequests[type];
		},

		/**
		 * Set pending request
		 *
		 * @param {Object} options - pending request options
		 * @param {string} options.type - type of pending request; valid values include: 'fetch'
		 * @param {string} options.cacheId - unique cache id to store the request; if the request already exists,
		 * @param {Promise} request - Promise that will resolve
		 */
		setPendingRequest: function(options, request) {

			if (!options) {
				throw new Error('Invalid options argument provided');
			}

			const { type, cacheId } = options;

			if (!cacheId) {
				throw new Error('Invalid cacheId option provided');
			}

			if (!this._isValidPendingRequestType(type)) {
				throw new Error(`Invalid type: ${type}`);
			}

			this._pendingRequests[type][cacheId] = request;
		},

		/**
		 * Get pending request if exists
		 *
		 * @param {Object} options - pending request options
		 * @param {string} options.type - type of pending request; valid values include: 'fetch'
		 * @param {string} options.cacheId - cache id the previous request was stored under
		 *
		 * @return {Promise} Promise that resolves with the result of the same request
		 */
		getPendingRequest: function(options) {
			if (!options) {
				throw new Error('Invalid options argument provided');
			}

			const { type, cacheId } = options;

			if (!cacheId) {
				throw new Error('Invalid cacheId option provided');
			}

			if (!this._isValidPendingRequestType(type)) {
				throw new Error(`Invalid type: ${type}`);
			}

			return this._pendingRequests[type][cacheId];
		},

		/**
		 * Initialize
		 *
		 * @param {object} options - options
		 * @param {GlassContext} options.glassContext - glass context
		 * @param {Object} [options.store] - store to use for the thumbnail store
		 * @param {Object} [options.elementMap] - element map
		 * @param {string[]} array of element excludeClassList
		 * @param {EchoCacheService} [options.echoCacheSvc] - echo cache service to use
		 *
		 * @return {Promise} promise resolved on success and rejected on failure
		 */
		initialize: function({
			store = {},
			elementMap,
			excludeClassList,
			echoCacheSvc
		}) {
			this._store = new ThumbnailStore(store);
			this.echoCacheService = echoCacheSvc || new EchoCacheService({
				logger: this.logger,
				ajaxSvc: this.ajax
			});
			this._thumbnailGenerator = null;

			this._elements = {
				map: {},
				paths: elementMap,
				excludeClassList: excludeClassList
			};

			return this.initializeThumbnailGenerator();
		},

		/**
		 * Initialize thumbnail generator
		 *
		 * @return {Promise} promise resolved on success and rejected on failure
		 */
		initializeThumbnailGenerator: function() {
			let promise;
			if (this._elements.paths) {
				const elementKeys = Object.keys(this._elements.paths);
				promise = Promise
					.all(
						elementKeys
							.map(key => `text!${this._elements.paths[key]}`)
							.map(path => ClassFactory.loadModule(path))
					)
					.then(elements => {
						elements.forEach((element, key) => {
							const mapKey = elementKeys[key];
							if (mapKey) {
								this._elements.map[mapKey] = element;
							}
						});
					})
					.catch(error => {
						this.logger.error('ThumbnailService.readyPromise:error', error);
					});
			} else {
				promise = Promise.resolve();
			}
			return promise
				.then(() => {
					this._thumbnailGenerator = new ImageCapture({
						UniqueId: UniqueId,
						elementMap: this._elements.map,
						excludeClassList: this._elements.excludeClassList
					});
				});
		},

		/**
		 * Destructor
		 */
		destroy: function() {
			if (this._thumbnailGenerator) {
				this._thumbnailGenerator.destroy();
			}
			this._elements = null;
			this._store = null;
			this.logger = null;
			this.ajax = null;
		},

		/**
		 * Add a thumbnail
		 *
		 * @param {string} widgetModel - widget model
		 * @param {any} data - thumbnail data
		 *
		 * @return {Promise} Promise to be resolved on success and rejected with error
		 */
		sync: function(widgetModel, data) {
			let result;
			if (data) {
				const updateToEchoService = (cacheId, data) => {
					return this.update(cacheId, data)
						.then(({ cacheId, affinity }) => this.fetch(cacheId, affinity))
						.then(({ data, cacheId }) => {
							this._setCacheIdInWidgetAndStore(widgetModel, cacheId, data);
						}, ()=>{
							this._setCacheIdInWidgetAndStore(widgetModel, null, null); //remove previous thumbnail on failure
						});
				};
				result = this.fetch(widgetModel.thumbnailId, undefined, { silent: true })
					.then(({ data: oldData })=>{
						if(this._needsUpdate(oldData, data)){
							return updateToEchoService(widgetModel.thumbnailId, data);
						} else {
							const cachedData = this._store.getLocal(widgetModel.id);
							if (this._needsUpdate(cachedData, data)) {
								this._setCacheIdInWidgetAndStore(widgetModel, widgetModel.thumbnailId, data);
							}
							return { data };
						}
					})
					.catch(()=>{
						return updateToEchoService(widgetModel.thumbnailId, data);
					});
			} else {
				this._setCacheIdInWidgetAndStore(widgetModel, null /*cacheId*/ );
			}
			return result || Promise.resolve();
		},

		_setCacheIdInWidgetAndStore: function(widgetModel, cacheId, data){
			const widgetId = widgetModel.id;
			const options = {
				payloadData: {
					runtimeOnly: true
				}
			};
			widgetModel.set({
				thumbnailId: cacheId
			}, options);

			if (cacheId) {
				this._store.set(widgetId, { data, cacheId });
			} else {
				this._store.delete(widgetId);
			}
		},

		/**
		 * Set error on the widget
		 *
		 * @param {string} widgetId - widget id
		 * @param {Error} error - thumbnail error to store
		 */
		setError: function(widgetId, error) {
			this._store.setError(widgetId, error);
		},

		/**
		 * Set warning on the widget
		 *
		 * @param {string} widgetId - widget id
		 * @param {Warning} warning - thumbnail warning to store
		 */
		setWarning: function(widgetId, warning) {
			this._store.setWarning(widgetId, warning);
		},

		/**
		 * Check whether the thumbnail is in dire need of an update
		 *
		 * @param {Object} data - old thumnail data
		 * @param {Object} newData - new thumbnail data
		 *
		 * @return {boolean} TRUE if needs update and FALSE if not
		 */
		_needsUpdate: function(data, newData){
			return this._stripUniqueAttributes(data) !== this._stripUniqueAttributes(newData) ||
				this._getImgSrc(data) !== this._getImgSrc(newData);
		},

		_getImgSrc: function(data) {
			const $data = $(data).find('img');
			if ($data) {
				return $data.attr('src');
			}
			return null;
		},
		/**
		 * Strip unique attributes from the data
		 *
		 * @param {string} data - input data
		 *
		 * @return {string} output data with the unique DOM attributes stripped
		 */
		_stripUniqueAttributes: function(data) {
			const $data = $(data);
			UNIQUE_DOM_ATTRIBUTES.forEach((attr) => {
				$data.find(`[${attr}]`).attr(attr, '');
			});
			return $data.html();
		},

		/**
		 * Get store API
		 *
		 * @param {Object} options
		 * @param {Function} options.defaultFn - default function to use in case there's no thumbnail with a specific id
		 *
		 * @return {object} store API
		 */
		getStoreAPI: function({ defaultFn } = { defaultFn: () => {} }) {
			// clone the store instance and assign the `getVisTypeDefault` method
			return _.extend({}, this._store, {
				getVisTypeDefault: defaultFn
			});
		},

		/**
		 * Get thumbnail base on the thumbnail model and vis type
		 *
		 * @param {WidgetModel} widgetModel - widget model
		 * @param {string} type - vis type
		 *
		 * @return {Promise}
		 */
		get: function(widgetModel, type, storeAPI) {

			if (!storeAPI) {
				storeAPI = this._store;
			}

			const id = widgetModel.id;

			let promise;
			if (storeAPI.exists(id)) {
				promise = Promise.resolve(
					storeAPI.getLocal(id)
				);
			} else {
				const cacheId = widgetModel.thumbnailId;
				if (cacheId) {
					promise = this.fetch(cacheId, undefined, { silent: true })
						.then(({ data, cacheId }) => {
							this._store.set(id, {
								data,
								cacheId
							});
							return storeAPI.getLocal(id);
						})
						.catch(() => {
							// retrieval from cache failed
							// but don't reject, recover
							return undefined;
						});
				} else {
					promise = Promise.resolve();
				}
			}
			return promise
				.then(() => storeAPI.get(id, type));
		},

		// --- AJAX methods

		/**
		 * Fetch a specific thumbnail
		 *
		 * @param {string} cacheId - the thumbnail id
		 * @param {string} [affinity] - server affinity to use (returned from the update call); cacheId and affinity are closely related. Making assumptions about the affinity is not recommended at this point.
		 * @param {object} [options={}] - options
		 * @param {boolean} [options.silent] - silent (don't log failures)
		 *
		 * @return {Promise} Promise that resolves with the thumbnail or rejects with error
		 */
		fetch: function(cacheId, affinity, options = {}) {

			if (!cacheId) {
				return Promise.reject(new Error(`Invalid cacheId argument provided: ${cacheId}`));
			}

			const pendingRequest = this.getPendingRequest({
				type: 'fetch',
				cacheId
			});
			if (pendingRequest) {
				const isPromise = !!pendingRequest.then;
				if (isPromise) {
					// if promise, return as is
					return pendingRequest;
				} else {
					// it's an error
					return Promise.reject(pendingRequest);
				}
			}

			const request = this.echoCacheService
				.retrieve(cacheId, affinity)
				.then(response => {

					// clear the pending request
					this.setPendingRequest({
						type: 'fetch',
						cacheId
					}, undefined);

					const { text } = response;
					this.logger.debug('ThumbnailService.fetch:response', response);
					return {
						cacheId,
						data: text
					};
				})
				.catch(response => {

					const error = this._ajaxErrorHandler(response, 'ThumbnailService.fetch:error', options);

					// save the error as a pending request, to prevent subsequent request before any update has finished
					// @see ThumbnailService::update method
					this.setPendingRequest({
						type: 'fetch',
						cacheId
					}, error);

					// reject the promise with the error.
					return Promise.reject(error);
				});

			this.setPendingRequest({
				type: 'fetch',
				cacheId
			}, request);

			return request;
		},

		/**
		 * Update a specific thumbnail
		 *
		 * @param {string} cacheId - the id of thumbnail
		 * @param {string} thumbnail - thumbnail data
		 * @param {object} [options={}] - options
		 * @param {boolean} [options.silent] - silent (don't log failures)
		 *
		 * @return {Promise} Promise that resolves with the thumbnail or rejects with error
		 */
		update: function(cacheId, thumbnail, options) {
			return this.echoCacheService
				.stash(thumbnail, {
					cacheId: cacheId
				})
				.then(({ id, location, affinity }) => {

					// clear the pending request on failure
					this.setPendingRequest({
						type: 'fetch',
						cacheId: id
					}, undefined);

					return {
						cacheId: id,
						location,
						affinity
					};
				})
				.catch(response => {
					return Promise.reject(this._ajaxErrorHandler(response, 'ThumbnailService.update:error', options));
				});
		},

		/**
		 * Ajax error handler
		 *
		 * @param {Object} response - error response about to be handled
		 * @param {string} logString - string used for logging
		 */
		_ajaxErrorHandler: function(response, logString = 'ThumbnailService:error', options = { silent: false }) {
			const { silent } = options;
			const message = this._parseError(response, StringResources.get('thumbnails_error_fetching'));
			const error = new Error(message);
			if (!silent) {
				this.logger.error(logString, error);
			}
			return error;
		},

		/**
		 * Parse error response
		 *
		 * @param {Object} response - ajax response
		 * @param {string} response.responseText - ajax response text
		 * @param {string} defaultMessage - default message in case there is no response text
		 *
		 * @return {string} final error text
		 */
		_parseError: function(response, defaultMessage) {
			let message = defaultMessage;
			try {
				if (response.responseText) {
					const text = JSON.parse(response.responseText);
					message = text.error;
				}
			} catch(e) {
				// ignore JSON parsing errors
			}
			return message;
		},

		/**
		 * Prepare thumbnail data
		 *
		 * @param {string} data - data to be prepared
		 *
		 * @return {string} prepared data
		 */
		_prepareThumbnail: function(data) {
			return JSON.stringify({ data });
		},

		/**
		 * Generate image
		 *
		 * @param {DOMElement} _domNode - DOM node to generate image of
		 * @param {object} options - options
		 * @param {number} maxHeight - maximum height
		 *
		 * @return {Promise} Promise to be resolved on success and rejected on faiure
		 */
		generateImage: function(_domNode, options, maxHeight) {
			options = _.extend(options || {}, {
				// the thumbnail generator takes the background color of selected node and applies them to
				// the thumbnail - we don't want this for nodes with static content, e.g. webpage widgets
				onClone: (doc, node) => {
					let $staticNode = $(node).find('.staticContent');
					if ($staticNode.length) {
						$staticNode.css({ 'background-color':'transparent' });
					}
				},
				excludeEmptyIframes: true,
				transparentBackground: false
			});
			return this._thumbnailGenerator.generateImage(_domNode, options, maxHeight);
		},

		/**
		 * Generate image
		 *
		 * @param {DOMElement} _domNode - DOM node to generate image of
		 * @param {object} options - options
		 * @param {number} maxHeight - maximum height
		 *
		 * @return {Promise} Promise to be resolved on success and rejected on faiure
		 */
		generateMarkup: function(_domNode, options, maxHeight) {
			return this.generateImage(_domNode, options, maxHeight)
				.then(content => `<div class="visThumbnail"><div class="visThumbnailImgContainer"><img src="${content}" /></div></div>`);
		},

		/**
		 * Delete thumbnail
		 *
		 * @param {id} thumbnail id
		 * @return {ThumbnailStore} self; current instance
		 */
		delete: function(id) {
			return this._store.delete(id);
		}
	});

	return ThumbnailService;
});
